import { useAutocomplete } from '../../hooks/search/useAutocomplete';
import classNames from 'classnames';
import { type Ref } from 'react';

export interface SearchAutocompleteProps {
  searchTerm: string;
  search: (searchTerm: string) => void;
  className?: string;
  id?: string;
  returnFocus: () => void;
  ref?: Ref<HTMLButtonElement>;
}

const SearchAutocomplete = ({
  searchTerm,
  search,
  className,
  id,
  returnFocus,
  ref,
}: SearchAutocompleteProps) => {
  const { data } = useAutocomplete(searchTerm);

  if (!data?.hits?.length) {
    return null;
  }

  return (
    <ul
      className={classNames(
        'list-none absolute top-full bg-neutral-600 inset-x-0 rounded-xl px-0 overflow-hidden',
        className,
      )}
      aria-label="Search suggestions"
      id={id}
    >
      {data.hits?.map(({ query }, i) => (
        <li key={query}>
          <button
            type="button"
            onClick={() => search(query)}
            aria-label={`Search for ${query}`}
            className="py-4 px-6 w-full text-left hover:bg-neutral-300 focus:bg-neutral-300"
            ref={i === 0 ? ref : undefined}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                (
                  e.currentTarget.parentElement?.nextElementSibling
                    ?.children?.[0] as HTMLButtonElement
                )?.focus();
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                (
                  e.currentTarget.parentElement?.previousElementSibling
                    ?.children?.[0] as HTMLButtonElement
                )?.focus();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                returnFocus();
              }
            }}
          >
            {query
              .split(new RegExp(`(${searchTerm.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')})`, 'g'))
              .map((part, i) => (part === searchTerm ? <b key={part + i}>{part}</b> : part))}
          </button>
        </li>
      ))}
    </ul>
  );
};

export default SearchAutocomplete;
